/**
 * HOME-03 — the Home page's photography, in one place.
 *
 * The hero and the "Browse by area" tiles both had their image props plumbed
 * through from the start of HOME-01 and never wired to anything, because the
 * repo held no assets and the two alternatives were both refused: a stock image
 * we hold no licence to, and an agent's listing photo promoted to site
 * furniture without them agreeing to it. Every surface therefore rendered the
 * brand panel instead — correct, and visibly a placeholder.
 *
 * This file is where the real photographs land. It is a build-time constant
 * rather than a table because the set is five or six images that change perhaps
 * twice a year: a table would add a query, a loading state and a failure mode
 * to the highest-traffic page in the product in exchange for an edit that is
 * currently a one-line commit. If that stops being true, this is the one file
 * to replace.
 *
 * EMPTY IS A VALID STATE and is the shipped default. A missing hero renders the
 * brand gradient; a missing area photo renders the brand-tinted tile with the
 * area's initial. Nothing here can produce a broken image, and adding a
 * photograph is a one-value change with no other code to touch.
 *
 * =============================================================================
 * SOURCE DIMENSIONS — what to export
 * =============================================================================
 *
 * HERO — one image
 *   Export at 2500 × 1100 (≈2.27:1), WebP or JPEG, target under ~300 KB.
 *   The banner renders at 1312 × 560 CSS px (the 1440 shell minus its 64px
 *   gutters), so the crop is ≈2.34:1 at every desktop width — it no longer
 *   slides with the viewport, which is what makes it composable at all. At
 *   2.27:1 the crop takes a few pixels off the top and bottom and nothing off
 *   the sides.
 *
 *   2500 IS A CEILING, NOT A PREFERENCE. Supabase's transform rejects any
 *   dimension above it, and a rejected srcset candidate is not retried at a
 *   smaller size — the browser gives up and the hero falls back to its
 *   gradient. Exporting at 2500 makes every candidate (828/1312/1968/2500) a
 *   downscale of the source, so nothing is upscaled and nothing is rejected.
 *
 *   COMPOSITION. At 1440 the search card (460px) and the proof card (280px)
 *   cover the LEFT ~800px of the banner. Put the subject in the RIGHT half and
 *   keep the left third quiet — sky, foliage, soft depth of field. There is no
 *   hero on mobile at all (`hidden lg:block`), so this asset is desktop-only.
 *
 * AREA TILES — one image per neighbourhood
 *   Export at 1200 × 600 (2:1), JPEG (sRGB), ~100–150 KB each.
 *
 *   THE CROP RANGE IS EXTREME, because the tile has a FIXED height (110 mobile,
 *   120 desktop) and a fluid width. Measured:
 *     1023  487 × 110 → 4.43:1   the two-column grid at its widest, just under
 *                                its `lg` breakpoint
 *      768  360 × 110 → 3.27:1
 *      430  191 × 110 → 1.74:1
 *     1024  164 × 120 → 1.37:1   the five-column grid at its narrowest
 *     1440  248 × 120 → 2.06:1   capped by the content shell
 *
 *   So one file is cropped anywhere from 1.37:1 to 4.43:1 under `object-fit:
 *   cover`. SAFE ZONE: the centre 822 × 271 of the 1200 × 600 source — 822 is
 *   what survives the narrowest crop (600 × 1.37) and 271 the widest
 *   (1200 ÷ 4.43). Centre the subject there; anything outside it is decoration
 *   that will sometimes be cut.
 *
 *   Delivered through `getAreaTileUrl` / `getAreaTileSrcSet` at 400–1000w.
 *
 *   The MOBILE tile also carries a dark bottom gradient with the area name and
 *   count in white over it, so keep the bottom-left quiet. On desktop the
 *   caption sits below the image and the whole frame is clear.
 *
 *   WHICH AREAS. The tiles are the top five by `public.home_area_counts`, which
 *   is live data and will reorder as the catalogue does. Supply more than five;
 *   an area with no entry here falls back to its brand tile, which is correct
 *   but visibly unlike its neighbours.
 *
 * KEYS MATCH `listings.neighborhood` EXACTLY, because that is what the view
 * groups by and what the tile is labelled with. Lookup is case-insensitive and
 * trimmed (see `areaImageUrl`) so "westlands" and "Westlands " both resolve,
 * but the canonical spelling is what the database holds.
 */

/**
 * The hero photograph. Null → the brand gradient.
 *
 * A Supabase storage URL (`/object/public/…`) is rewritten to the render
 * endpoint automatically, which is what gives the hero its resized srcset; any
 * other absolute URL (a CDN) is served AS-IS, with no resizing and four
 * identical srcset candidates. So this belongs in Supabase storage.
 *
 * IF THE RENDER ENDPOINT IS UNAVAILABLE for this bucket — image transformation
 * not enabled on the project, or the bucket not public — the request fails and
 * the `<img>` renders nothing (its `alt` is empty by design), leaving the brand
 * gradient behind it. The hero degrades to exactly what it looked like before a
 * photograph existed rather than to a broken-image icon or a white hole.
 *
 * THE SOURCE IS 2500 × 1100, which is deliberate on both axes:
 *   2500  the ceiling Supabase's transform accepts, so every srcset candidate
 *         (828/1312/1968/2500) is a DOWNSCALE. Nothing is ever upscaled, and
 *         the largest candidate is a no-op resize rather than a rejected
 *         request — see SUPABASE_MAX_TRANSFORM_DIMENSION in utils/imageUrl.ts.
 *   1100  ≈2.27:1 against the banner's 2.34:1, so the crop is a few pixels off
 *         the top and bottom and nothing off the sides. The composition the
 *         photograph was framed for survives.
 *
 * JPEG, NOT PNG, and that is the second upload. The first was a PNG and
 * rendered black and white on the deployed preview — nothing in this repo
 * desaturates it (the only `grayscale` in the codebase is on sold listings in
 * the agent dashboard), so it was the delivered bytes, from the export or from
 * the render endpoint's handling of that file's colour profile. Re-exporting as
 * JPEG/sRGB settles it either way, and a 2500px photograph has no business
 * being a PNG regardless: PNG is lossless, so it was paying a large transfer
 * cost for a fidelity nobody can see behind two opaque cards.
 *
 * The filename is the uploader's and is kept verbatim rather than guessed at.
 * Its spaces and parentheses survive URL-encoding; a stable `home-hero.jpg`
 * would still be easier to live with the next time this is replaced.
 */
export const HOME_HERO_IMAGE_URL: string | null =
  'https://zxsnritknqktyixcapmt.supabase.co/storage/v1/object/public/brand-assets/HERO%201%20(2500%20x%201100%20px).jpg';

/**
 * Neighbourhood → photograph. Absent → the brand tile with the area's initial.
 * Keys are `listings.neighborhood` values.
 */
const BRAND_ASSETS =
  'https://zxsnritknqktyixcapmt.supabase.co/storage/v1/object/public/brand-assets';

export const HOME_AREA_IMAGE_URLS: Readonly<Record<string, string>> = {
  Westlands: `${BRAND_ASSETS}/Westlands.jpg`,
  Kilimani: `${BRAND_ASSETS}/Kilimani.jpg`,
  Nyali: `${BRAND_ASSETS}/Nyali.jpg`,
  Kileleshwa: `${BRAND_ASSETS}/Kileleshwa.jpg`,
  Lavington: `${BRAND_ASSETS}/Lavington.jpg`,
  Karen: `${BRAND_ASSETS}/Karen.jpg`,
  // Runda is below the top five today, so this tile does not render yet. It is
  // here because the row is ranked by LIVE counts and Runda is the next area
  // likely to enter it — supplying the photograph before that happens is the
  // difference between the tile arriving finished and arriving as a panel.
  Runda: `${BRAND_ASSETS}/Runda.jpg`,
};

/**
 * The photograph for an area, or null.
 *
 * Case-insensitive and trimmed: the view returns whatever `neighborhood` holds,
 * and one stray capital in the database should not silently drop a photograph
 * that was supplied for it.
 */
export function areaImageUrl(name: string): string | null {
  const key = (name || '').trim().toLowerCase();
  if (!key) return null;
  for (const [area, url] of Object.entries(HOME_AREA_IMAGE_URLS)) {
    if (area.trim().toLowerCase() === key) return url;
  }
  return null;
}
